import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import type { CollectionMall } from '@/features/catalog/productCollection/collectionMalls';
import type {
  EventCollectionField,
  EventCollectionFieldPayload,
} from '../types/collectionField';

/**
 * ID23 이벤트 수집 항목 API. (be-catalog 계약.) 이벤트 하위 리소스로,
 * /admin/v1/events/{eventId}/collection-fields. envelope { success, data }.
 *
 * 항목2 — 4몰별 분리. 목록은 `?mall=<BCP-47>` 필터, 생성/수정은 payload.mall 동반
 * (order_form_fields 미러: 행단위 CRUD 라 mall 은 각 행에 귀속). EventConsentBlock 은
 * enum(테이블 없음)이라 mall 무관 공통 — 동의는 본 폼에서 다루지 않는다.
 */
const BASE = '/admin/v1/events';

const path = (eventId: string) => `${BASE}/${eventId}/collection-fields`;
const listPath = (eventId: string, mall: CollectionMall) =>
  `${path(eventId)}?mall=${encodeURIComponent(mall)}`;

export const collectionFieldsQueryKey = (eventId: string, mall: CollectionMall) =>
  ['admin', 'event-collection-fields', eventId, mall] as const;

export const useEventCollectionFieldsQuery = (eventId: string, mall: CollectionMall) =>
  useQuery<EventCollectionField[]>({
    queryKey: collectionFieldsQueryKey(eventId, mall),
    queryFn: async () => {
      if (!eventId || eventId === 'new') return [];
      const res = await adminFetch(listPath(eventId, mall));
      if (!res.ok) throw new Error(`collection fields fetch failed: ${res.status}`);
      const env = (await res.json()) as { data?: EventCollectionField[] | null };
      return Array.isArray(env.data) ? env.data : [];
    },
    enabled: !!eventId && eventId !== 'new',
  });

export const useEventCollectionFieldMutation = (eventId: string, mall: CollectionMall) => {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: collectionFieldsQueryKey(eventId, mall) });

  const create = useMutation({
    mutationFn: async (body: EventCollectionFieldPayload): Promise<void> => {
      // be-catalog 확정 계약(62c007d): 생성 mall 은 `?mall=` 쿼리로 귀속. body 도
      // mall 을 동반하나(EventCollectionFieldRequest.mall) 쿼리가 권위.
      const p = listPath(eventId, mall);
      const res = await adminFetch(p, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', p, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`collection field create failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({
      fieldId,
      body,
    }: {
      fieldId: string;
      body: EventCollectionFieldPayload;
    }): Promise<void> => {
      const p = `${path(eventId)}/${fieldId}`;
      const res = await adminFetch(p, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', p, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`collection field update failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (fieldId: string): Promise<void> => {
      const p = `${path(eventId)}/${fieldId}`;
      const res = await adminFetch(p, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': idempotencyKeyFor('DELETE', p, '') },
      });
      if (!res.ok) throw new Error(`collection field delete failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  return { create, update, remove };
};

/**
 * ID23 / QA-137 응모자 목록 xlsx 추출 (GET .../applications/export). blob → a[download].
 *
 * 컬럼(QA-137 신규 템플릿, BE 결정): 주문번호/회원명/주문상태/상품명/이벤트 옵션 이름/생년월일/
 * 연락처/이메일/국적/상품수량. (수집필드별 동적컬럼은 BE 가 민감값 복호화해 채운다.)
 *
 * `paidOnly` — QA-137 "결제 완료 상품만 추출" 옵션. true 면 `?paidOnly=true` 를 실어
 * 결제완료(PAID) 주문의 응모만 추출한다. BE 미지원 시 무시되며 전체 추출로 동작(안전).
 * (파라미터명은 be-event 와 합의 — 합의 결과에 맞춰 EXPORT_PAID_ONLY_PARAM 만 변경하면 됨.)
 */
export const EXPORT_PAID_ONLY_PARAM = 'paidOnly';

export interface EventApplicationsExportOptions {
  paidOnly?: boolean;
  /**
   * QA-416 — 'APPLICATIONS' = 응모 목록 양식(이벤트명·응모번호·응모일·회원 ID·회원명·이메일·수량·주문번호·
   * 이벤트 수집 항목·상품 추가입력, 전체 응모, 응모일 순). 생략 = 응모자 추출 양식(QA-137 고도몰 템플릿, BE catalog #64).
   */
  layout?: 'APPLICATIONS';
}

export const downloadEventApplications = async (
  eventId: string,
  options: EventApplicationsExportOptions = {},
): Promise<{ blob: Blob; filename: string }> => {
  const params = new URLSearchParams();
  if (options.paidOnly) params.set(EXPORT_PAID_ONLY_PARAM, 'true');
  if (options.layout) params.set('layout', options.layout);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await adminFetch(`${BASE}/${eventId}/applications/export${qs}`);
  if (!res.ok) throw new Error(`event applications export failed: ${res.status}`);
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
  const filename = match?.[1]
    ? decodeURIComponent(match[1])
    : `event-applications-${eventId}.xlsx`;
  return { blob, filename };
};
